import type { CSSProperties } from "react";
import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyMessageBar,
  SkyText,
  type SkyBadgeColor,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import {
  MAX_RUN_TRANSFORM_NODES,
  type RunStatus,
  type StepStatus,
  type RunTransformModelResult,
  type RunTransformResults,
  type RunTransformTestResult,
} from "@shared/types/runs";
import { dbtNoRowsLabel } from "@shared/types/dbtCauses";
import { QuietMessageBar } from "@/components/overview/QuietMessageBar";
import { CauseBlock, failuresUnit } from "@/components/transformations/CauseBlock";
import { Dash } from "@/components/transformations/TransformationModelsView";
import { failureMessage } from "@/lib/api";
import { pixelColumnLayout } from "@/lib/grid";
import { BREAKABLE_NAME_CLASS, breakableName, breakableNodeId } from "@/lib/breakableName";
import {
  modelsEmptyMessage,
  testsEmptyMessage,
  transformStepFacts,
} from "@/lib/transformations/emptyResults";
import { formatCount, formatStageSeconds } from "./runFormatting";

/**
 * The run page's Transformations view (Session 10, P11; ux-s10.md §1, SF7): this run's dbt models
 * and tests as the server reads them (`GET /runs/:runId/transform-results`, the current-pass
 * rule), never rebuilt from raw events here. Failures come first, each with its classified cause
 * (`CauseBlock`, the same block the transformation page uses); statuses are words and colours.
 */

const STATUS_WORDS: Record<string, { label: string; color: SkyBadgeColor }> = {
  success: { label: "Built", color: "success" },
  pass: { label: "Passed", color: "success" },
  "no-op": { label: "No change", color: "subtle" },
  warn: { label: "Warning", color: "warning" },
  skipped: { label: "Skipped", color: "warning" },
  error: { label: "Failed", color: "danger" },
  fail: { label: "Failed", color: "danger" },
  "runtime error": { label: "Failed", color: "danger" },
  "partial success": { label: "Partly built", color: "danger" },
  unknown: { label: "Unknown", color: "subtle" },
};

function StatusCell({ status }: { status: string }) {
  const word = STATUS_WORDS[status] ?? { label: status, color: "subtle" as const };
  return (
    <SkyBadge appearance="tint" color={word.color}>
      {word.label}
    </SkyBadge>
  );
}

/**
 * Walk of 9a9d586 (D-h, 1280 px): the name and the node id were cut ("test.skydata_saas_test.
 * not_null_customer_orders_total_amou...") with nothing to read the rest by. Both wrap now, the
 * name after an underscore and the id after an underscore or a dot (`client/AGENTS.md`: a name
 * in a grid wraps rather than being cut).
 */
function NameCell({ name, node }: { name: string; node: string }) {
  return (
    <div className="min-w-0">
      <SkyText size={300} weight="semibold" block className={BREAKABLE_NAME_CLASS}>
        {breakableName(name)}
      </SkyText>
      <SkyText size={200} color="muted" font="monospace" block className={BREAKABLE_NAME_CLASS}>
        {breakableNodeId(node)}
      </SkyText>
    </div>
  );
}

/**
 * #1196: a measured floor under the results grids. Their columns are shares of the table, so a
 * narrow page (1024) squeezed "Built as" and "Status" below their own header. At 600px the
 * smallest share, Time at 14% (90px), still holds its header and padding, and a page narrower
 * than the floor scrolls the table instead of cutting a header.
 */
const RESULTS_TABLE_FLOOR = "[&_table]:min-w-[600px]";

const MODEL_COLUMNS: SkyDataGridColumn<RunTransformModelResult>[] = [
  {
    key: "name",
    header: "Model",
    width: "30%",
    renderCell: (_value, row) => (
      <div className="flex flex-col gap-1">
        <NameCell name={row.name} node={row.node} />
        {row.cause ? (
          <CauseBlock
            cause={row.cause}
            compact
            whyOnlyWhenGeneric
            testId={`run-model-cause-${row.name}`}
          />
        ) : null}
      </div>
    ),
  },
  {
    key: "status",
    header: "Status",
    // #1196: pixel widths hold the whole header at 1024.
    width: "128px",
    renderCell: (_value, row) => <StatusCell status={row.status} />,
  },
  {
    key: "materialization",
    header: "Built as",
    width: "120px",
    renderCell: (_value, row) => <SkyText size={200}>{row.materialization ?? "—"}</SkyText>,
  },
  {
    key: "executionTimeSeconds",
    header: "Time",
    width: "14%",
    align: "right",
    renderCell: (_value, row) => (
      <SkyText size={200} font="numeric">
        {formatStageSeconds(row.executionTimeSeconds)}
      </SkyText>
    ),
  },
  {
    key: "rowsAffected",
    header: "Rows",
    width: "22%",
    align: "right",
    renderCell: (_value, row) => {
      // #1093: the one rule for a kind that reports no row count (`dbtNoRowsLabel`).
      const noRows = dbtNoRowsLabel(row.materialization);
      return noRows ? (
        <Dash label={noRows} />
      ) : (
        <SkyText size={200} font="numeric">
          {formatCount(row.rowsAffected)}
        </SkyText>
      );
    },
  },
];

const MODEL_LAYOUT = pixelColumnLayout(MODEL_COLUMNS, {
  minimums: { name: 200, executionTimeSeconds: 80, rowsAffected: 96 },
  selection: false,
});

const TEST_COLUMNS: SkyDataGridColumn<RunTransformTestResult>[] = [
  {
    key: "name",
    header: "Test",
    width: "40%",
    renderCell: (_value, row) => (
      <div className="flex flex-col gap-1">
        <NameCell name={row.name} node={row.node} />
        {row.cause ? (
          <CauseBlock
            cause={row.cause}
            compact
            whyOnlyWhenGeneric
            testId={`run-test-cause-${row.name}`}
          />
        ) : null}
      </div>
    ),
  },
  {
    key: "status",
    header: "Status",
    width: "16%",
    renderCell: (_value, row) => <StatusCell status={row.status} />,
  },
  {
    key: "kind",
    header: "Kind",
    width: "16%",
    renderCell: (_value, row) => (
      <SkyText size={200}>
        {row.kind ?? "—"}
        {row.column ? ` · ${row.column}` : ""}
      </SkyText>
    ),
  },
  {
    key: "failures",
    header: "Failures",
    width: "14%",
    align: "right",
    renderCell: (_value, row) => (
      <SkyText size={200} font="numeric">
        {row.failures === null
          ? "—"
          : `${formatCount(row.failures)} ${failuresUnit(
              {
                ...(row.cause ? { cause: row.cause } : {}),
                ...(row.kind ? { kind: row.kind } : {}),
              },
              row.failures,
            )}`}
      </SkyText>
    ),
  },
  {
    key: "executionTimeSeconds",
    header: "Time",
    width: "14%",
    align: "right",
    renderCell: (_value, row) => (
      <SkyText size={200} font="numeric">
        {formatStageSeconds(row.executionTimeSeconds)}
      </SkyText>
    ),
  },
];

export interface TransformResultsViewProps {
  data: RunTransformResults | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
  /** The run's status, for the empty grids' words; absent reads as a run still active. */
  runStatus?: RunStatus;
  /**
   * The run's transform step, for the empty grids' words (`transformStepFacts`): its status, its
   * skip reason and its summary counts.
   */
  transformStep?: { status: StepStatus; metrics: unknown; startedAt?: string | null } | undefined;
}

export function TransformResultsView({
  data,
  isLoading,
  error,
  onRetry,
  runStatus,
  transformStep,
}: TransformResultsViewProps) {
  const facts = { runStatus, ...transformStepFacts(transformStep) };
  if (error && !data) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        title="We could not load this run's models and tests"
        message={failureMessage(error, "Try again.")}
        actions={
          <SkyButton size="small" onClick={onRetry}>
            Try again
          </SkyButton>
        }
        testId="run-transform-error"
      />
    );
  }

  const models = data?.models ?? [];
  const tests = data?.tests ?? [];
  const failedModels = models.filter((model) => model.failed).length;
  const failedTests = tests.filter((test) => test.failed).length;
  // No count until the first read answers: "Models (0)" while loading would say none reported.
  const heading = (noun: string, total: number, failed: number): string => {
    if (!data) return noun;
    const failedPart = failed > 0 ? `, ${formatCount(failed)} failed` : "";
    return `${noun} (${formatCount(total)}${failedPart})`;
  };

  return (
    <div className="flex flex-col gap-4" data-testid="run-transform-results">
      {data?.truncated ? (
        // Quiet like every other bar on the run page: the page's one polite line speaks (S2).
        <QuietMessageBar
          intent="info"
          layout="multiline"
          message={`Only the first ${formatCount(MAX_RUN_TRANSFORM_NODES)} models and tests are listed, failures first.`}
          testId="run-transform-truncated"
        />
      ) : null}
      <section aria-label="Models" className="flex flex-col gap-2">
        <SkyText as="h3" size={300} weight="semibold" block>
          {heading("Models", models.length, failedModels)}
        </SkyText>
        <div style={{ "--models-grid-floor": `${MODEL_LAYOUT.floor}px` } as CSSProperties}>
          <SkyDataGrid<RunTransformModelResult>
            data={models}
            columns={MODEL_LAYOUT.columns}
            className="[&_table]:min-w-(--models-grid-floor)"
            getRowId={(row) => row.node}
            loading={isLoading}
            emptyMessage={modelsEmptyMessage(facts)}
            testId="run-transform-models"
          />
        </div>
      </section>
      <section aria-label="Tests" className="flex flex-col gap-2">
        <SkyText as="h3" size={300} weight="semibold" block>
          {heading("Tests", tests.length, failedTests)}
        </SkyText>
        <SkyDataGrid<RunTransformTestResult>
          data={tests}
          columns={TEST_COLUMNS}
          getRowId={(row) => row.node}
          loading={isLoading}
          emptyMessage={testsEmptyMessage(data?.testsRequested, facts)}
          className={RESULTS_TABLE_FLOOR}
          testId="run-transform-tests"
        />
      </section>
    </div>
  );
}
